"use client";
// Edad de cada menor y la regla del tour (cortesías, tarifas por edad). La usan la página de reservas, el carrito y la app de vendedores.
import { useEffect, useState } from "react";
import { describirTramos, precioMenor, type Tramo } from "@/lib/menores";
import { formatoMXN } from "@/lib/dinero";

export type ReglasMenores = { configurado: boolean; tramos: Tramo[]; adultoCentavos: number; ninoCentavos: number };
export type Edad = number | "";

/** Reglas de menores del tour (se piden una vez; sin conexión o en la vista previa no hay reglas y rige el precio de menor de siempre). */
export function useReglasMenores(slug: string, activo = true) {
  const [r, setR] = useState<ReglasMenores | null>(null);
  useEffect(() => {
    if (!activo || process.env.NEXT_PUBLIC_EXPORT === "1") { setR(null); return; }
    const clave = `sierra-menores-${slug}`; let vivo = true;
    fetch(`/api/menores?tour=${encodeURIComponent(slug)}`).then((x) => (x.ok ? x.json() : null)).then((d) => {
      if (!vivo) return;
      if (d) { setR(d); try { sessionStorage.setItem(clave, JSON.stringify(d)); } catch { /* sin almacenamiento */ } }
    }).catch(() => { if (!vivo) return; try { const g = sessionStorage.getItem(clave); setR(g ? JSON.parse(g) : null); } catch { setR(null); } });
    return () => { vivo = false; };
  }, [slug, activo]);
  return r;
}

/** Mantiene una edad por menor al subir o bajar la cantidad. */
export function ajustarEdades(edades: Edad[], ninos: number): Edad[] {
  return ninos > edades.length ? [...edades, ...Array<Edad>(ninos - edades.length).fill("")] : edades.slice(0, ninos);
}
export const edadesListas = (edades: Edad[], ninos: number) => ninos === 0 || (edades.length === ninos && edades.every((e) => e !== ""));

const EDAD_TOPE = 17;
/** Rangos para elegir la edad con botones: los del tour si están configurados y, si no, de cinco en cinco. Toda edad de 0 a 17 queda en algún grupo. */
export function rangosDeEdad(tramos: Tramo[] | undefined): { desde: number; hasta: number }[] {
  const base = tramos?.length ? [...tramos].sort((a, b) => a.desde - b.desde).map((x) => ({ desde: Math.max(0, x.desde), hasta: Math.min(EDAD_TOPE, x.hasta) })).filter((x) => x.desde <= x.hasta) : [{ desde: 0, hasta: 4 }, { desde: 5, hasta: 9 }, { desde: 10, hasta: 14 }, { desde: 15, hasta: EDAD_TOPE }];
  const dentro = (a: number) => base.some((r) => a >= r.desde && a <= r.hasta);
  const sueltas = Array.from({ length: EDAD_TOPE + 1 }, (_, a) => a).filter((a) => !dentro(a));
  // las edades sin rango se agrupan en tramos continuos (12 a 17), no una por una
  const huecos: { desde: number; hasta: number }[] = [];
  for (const a of sueltas) { const ultimo = huecos.at(-1); if (ultimo && ultimo.hasta === a - 1) ultimo.hasta = a; else huecos.push({ desde: a, hasta: a }); }
  return [...base, ...huecos].sort((a, b) => a.desde - b.desde);
}
/** La edad que se guarda cuando se elige un rango: la de abajo. Basta con el rango; la tarifa la define el tour. */
export const edadDeRango = (r: { desde: number }) => r.desde;
const textoRango = (r: { desde: number; hasta: number }, es: boolean) => r.desde === r.hasta ? `${r.desde} ${r.desde === 1 ? (es ? "año" : "year") : (es ? "años" : "years")}` : `${r.desde} ${es ? "a" : "to"} ${r.hasta} ${es ? "años" : "years"}`;

export function EdadesMenores({ idioma, edades, cambiar, reglas, compacto = false }: { idioma: "es" | "en"; edades: Edad[]; cambiar: (e: Edad[]) => void; reglas: ReglasMenores | null; compacto?: boolean }) {
  const es = idioma === "es";
  const [abierto, setAbierto] = useState<number | null>(null); // menor ya elegido que se reabre para cambiarlo
  if (edades.length === 0) return null;
  const nota = (e: Edad) => {
    if (e === "" || !reglas) return "";
    const m = precioMenor(e, reglas.tramos, reglas.adultoCentavos);
    if (m.comoAdulto) return es ? "Tarifa de adulto" : "Adult fare";
    return m.cortesia ? (m.ocupaLugar ? (es ? "Cortesía" : "Free") : (es ? "Cortesía · viaja en piernas, sin lugar" : "Free · on lap, no seat")) : formatoMXN(m.centavos, idioma);
  };
  return (
    <div className={`edades-menores${compacto ? " compacto" : ""}`}>
      <p className="edades-t"><b>{compacto ? (es ? "¿En qué rango está cada menor?" : "Which age range is each child in?") : (es ? "¿Qué edad tiene cada menor?" : "How old is each child?")}</b></p>
      {reglas?.configurado && !compacto && <ul className="edades-regla">{describirTramos(reglas.tramos, reglas.adultoCentavos, (c) => formatoMXN(c, idioma), es).map((l) => <li key={l}>{l}</li>)}</ul>}
      {compacto ? <div className="edades-tactil">
        {edades.map((e, i) => {
          const rangos = rangosDeEdad(reglas?.configurado ? reglas.tramos : undefined), elegido = e === "" ? undefined : rangos.find((r) => e >= r.desde && e <= r.hasta);
          if (elegido && abierto !== i) return ( // ya elegido: una sola línea, para no alargar la pantalla
            <button key={i} type="button" className="edades-resumen" aria-expanded="false" onClick={() => setAbierto(i)}>
              <b>{es ? "Menor" : "Child"} {i + 1}</b><span>{textoRango(elegido, es)}</span><small>{nota(elegido.desde)}</small><em>{es ? "Cambiar" : "Change"}</em>
            </button>);
          return (
            <fieldset key={i} className="edades-menor">
              <legend>{es ? "Menor" : "Child"} {i + 1}</legend>
              <div className="edades-rangos">
                {rangos.map((r) => <button key={r.desde} type="button" aria-pressed={elegido === r} onClick={() => { cambiar(edades.map((v, j) => (j === i ? edadDeRango(r) : v))); setAbierto(null); }}>
                  <b>{textoRango(r, es)}</b><small>{nota(r.desde) || " "}</small>
                </button>)}
              </div>
            </fieldset>);
        })}
      </div> : <div className="edades-lista">
        {edades.map((e, i) => (
          <label key={i}>
            <span>{es ? "Menor" : "Child"} {i + 1}</span>
            <select value={e} required onChange={(x) => cambiar(edades.map((v, j) => (j === i ? (x.target.value === "" ? "" : Number(x.target.value)) : v)))} aria-label={`${es ? "Edad del menor" : "Age of child"} ${i + 1}`}>
              <option value="">{es ? "Edad…" : "Age…"}</option>
              {Array.from({ length: 18 }, (_, a) => <option key={a} value={a}>{a === 0 ? (es ? "Menos de 1 año" : "Under 1") : `${a} ${a === 1 ? (es ? "año" : "year") : (es ? "años" : "years")}`}</option>)}
            </select>
            {e !== "" && <small>{nota(e)}</small>}
          </label>
        ))}
      </div>}
    </div>
  );
}
